import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ListDTO } from 'src/app/shared/loading-state/api.models';
import {
  ReferralCodeResponse,
  RewardDTO,
  UserGroup,
  UserGroupDTO,
  UserProfile,
  UserProfileForm,
} from '../../../auth/utility/auth.models';
import { HttpClientWrapper } from '../../../http-client-wrapper';
import {
  Invite,
  InviteDTO,
  InviteValidationResponse,
  Permission,
  Provider,
  VehicleAccess,
} from '../../admin.models';
import {
  billingCardUrl,
  billingDetailsUrl,
  invitesFormUrl,
  invitesUrl,
  permissionsGroupsUrl,
  permissionsListUrl,
  permissionsOwnershipTransferUrl,
  permissionsRequestTransferUrl,
  providersUrl,
  referralActivateUrl,
  referralCodeUrl,
  referralPromoResolveUrl,
  referralRewardsIssueUrl,
  referralRewardsUrl,
  resolveReferralUrl,
  signupUserUrl,
  subscriptionInvoicesUrl,
  usersUrl,
  userUrl,
  vehicleAccessUrl
} from '../../admin.urls';
import {
  BillingCardDTO,
  BillingDetailsDTO,
} from '../../components/payment-form/payment-form.models';
import { SubscriptionInvoiceDTO } from '../../pages/payment-history/payment-history.models';

@Injectable({ providedIn: 'root' })
export class AdminDataService {
  constructor(private httpClientWrapper: HttpClientWrapper) {}

  // Providers (OEM)

  getProviders(): Observable<Provider[]> {
    return this.httpClientWrapper.get(providersUrl);
  }

  // Users

  getUsers(): Observable<UserProfile[]> {
    return this.httpClientWrapper.get(usersUrl);
  }

  createUserNew(user: UserProfileForm, invite_id?: string): Observable<{ user: UserProfile }> {
    return this.httpClientWrapper.post(userUrl, { user, invite_id });
  }

  createUser(
    user: UserProfileForm,
    invite_id?: string,
    referral_code?: string,
    promo?: string
  ): Observable<{ user: UserProfile }> {
    return this.httpClientWrapper.post(signupUserUrl, { user, invite_id, referral_code, promo });
  }

  deleteUser(user: UserProfile): Observable<UserProfile[]> {
    return this.httpClientWrapper.delete(usersUrl + user.id.toString(), {});
  }

  updateUserGroups(
    user: number,
    groups: number[],
    editor_email?: string
  ): Observable<UserProfile[]> {
    return this.httpClientWrapper.put(usersUrl + 'group_assign/', { user, groups, editor_email });
  }

  // Invites

  getInvites(): Observable<Invite[]> {
    return this.httpClientWrapper.get(invitesFormUrl);
  }

  validateInvite(invite_id: string, email?: string): Observable<InviteValidationResponse> {
    return this.httpClientWrapper.get(invitesUrl + invite_id, email ? { email } : undefined);
  }

  createInvite(invite: InviteDTO): Observable<{ success: boolean }> {
    return this.httpClientWrapper.post(invitesFormUrl, invite);
  }

  deleteInvite(invite_id: string): Observable<{ success: boolean }> {
    return this.httpClientWrapper.delete(invitesFormUrl + invite_id + '/', {});
  }

  joinThroughInvite(invite_id: string): Observable<{ success: boolean }> {
    return this.httpClientWrapper.put(invitesFormUrl + invite_id + '/', {});
  }

  emailInvite(invite_id: string, recipient_emails: string[], role: string) {
    const params = {
      invite_id: invite_id,
      email_addresses: recipient_emails,
      role: role,
    };
    return this.httpClientWrapper.post(invitesFormUrl + 'email_invite/', params);
  }

  // Permissions

  getPermissionsList(): Observable<Permission[]> {
    return this.httpClientWrapper.get(permissionsListUrl);
  }

  getPermissionGroups(): Observable<UserGroup[]> {
    return this.httpClientWrapper.get(permissionsGroupsUrl);
  }

  deletePermissionGroup(id: number): Observable<{ success: boolean }> {
    return this.httpClientWrapper.delete(`${permissionsGroupsUrl}${id}/`, {});
  }

  getMemberCounts(): Observable<{ name: string; member_count: number }[]> {
    return this.httpClientWrapper.get(permissionsGroupsUrl + 'member_counts/');
  }

  createPermissionGroup(group: UserGroupDTO): Observable<UserGroup[]> {
    return this.httpClientWrapper.post(permissionsGroupsUrl, { ...group });
  }

  validateOwnershipTransfer(transfer_id: string): Observable<InviteValidationResponse> {
    return this.httpClientWrapper.get(permissionsOwnershipTransferUrl, { transfer_id });
  }

  requestOwnershipTransfer(email: string): Observable<string> {
    return this.httpClientWrapper.post(permissionsRequestTransferUrl, { email });
  }

  acceptOwnershipTransfer(transfer_id: string): Observable<string> {
    return this.httpClientWrapper.post(permissionsOwnershipTransferUrl, { transfer_id });
  }

  declineOwnershipTransfer(transfer_id: string): Observable<string> {
    return this.httpClientWrapper.delete(`${permissionsOwnershipTransferUrl}${transfer_id}/`, {});
  }

  // Billing

  getBillingDetails(): Observable<BillingDetailsDTO> {
    return this.httpClientWrapper.get<BillingDetailsDTO>(billingDetailsUrl);
  }

  getBillingCard(): Observable<BillingCardDTO> {
    return this.httpClientWrapper.get<BillingCardDTO>(billingCardUrl);
  }

  getSubscriptionInvoices(
    pageIndex: number,
    pageSize: number
  ): Observable<ListDTO<SubscriptionInvoiceDTO>> {
    return this.httpClientWrapper.get<ListDTO<SubscriptionInvoiceDTO>>(subscriptionInvoicesUrl, {
      page: pageIndex,
      page_size: pageSize,
    });
  }

  getVehicleAccess(email?: string): Observable<VehicleAccess[] | null> {
    if (email) return this.httpClientWrapper.get(vehicleAccessUrl, { email: email });
    return of(null);
  }

  setVehicleAccess(
    vehicle_ids: number[],
    fleet_ids?: number[],
    all_vehicles?: boolean,
    email?: string,
    user?: UserProfile
  ): Observable<{ success: boolean }> {
    return this.httpClientWrapper.post(vehicleAccessUrl, {
      vehicle_ids: vehicle_ids,
      fleet_ids: fleet_ids,
      all_vehicles: all_vehicles,
      email: email,
      user: user?.id,
    });
  }

  deleteVehicleAccess(vehicle_id: number, user: UserProfile): Observable<{ success: boolean }> {
    return this.httpClientWrapper.get(vehicleAccessUrl, {
      vehicle_id: vehicle_id,
      user_id: user.id,
    });
  }

  getAllUsersAccess(): Observable<VehicleAccess[]> {
    return this.httpClientWrapper.get(vehicleAccessUrl + 'get_all_users_access/');
  }

  // Referrals

  getReferralCode(): Observable<ReferralCodeResponse> {
    return this.httpClientWrapper.get<ReferralCodeResponse>(referralCodeUrl);
  }

  activateReferral(platform?: 'stripe' | 'ios' | 'playstore'): Observable<{ ok: boolean }> {
    return this.httpClientWrapper.post<{ ok: boolean }>(
      referralActivateUrl,
      platform ? { platform } : {}
    );
  }

  resolveReferral(code: string): Observable<{ full_name: string; email: string }> {
    return this.httpClientWrapper.get<{ full_name: string; email: string }>(resolveReferralUrl, {
      code,
    });
  }

  getReferralRewards(): Observable<RewardDTO[]> {
    return this.httpClientWrapper.get<RewardDTO[]>(referralRewardsUrl);
  }

  issuePendingReferralRewards(): Observable<{ issued_ids: number[] }> {
    return this.httpClientWrapper.post<{ issued_ids: number[] }>(referralRewardsIssueUrl, {});
  }

  buildReferralLink(code: string): string {
    return `${window.location.origin}/auth/signup?ref=${code}`;
  }

  // Promo

  resolvePromo(
    token: string,
    maxAgeSeconds = 14 * 24 * 3600
  ): Observable<{ months: number; days: number }> {
    return this.httpClientWrapper.get<{ months: number; days: number }>(referralPromoResolveUrl, {
      token,
      max_age_seconds: maxAgeSeconds,
    });
  }
}
